import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { commitMutation, graphql } from 'react-relay';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSpinner } from '@fortawesome/pro-regular-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  avatarWrap: {
    position: 'relative',
    zIndex: 1,
    background: 'white',
    borderRadius: '50%',
    boxShadow: '0 0 0 4px white, 0 2px 8px rgba(0, 0, 0, 0.08)'
  },
  input: {
    display: 'none'
  },
  cameraBadge: {
    position: 'absolute',
    right: -4,
    bottom: -4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#0b57ff',
    border: '2px solid white',
    color: 'white',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.15s',
    '&:hover': {
      backgroundColor: '#0a4cdf',
      transform: 'scale(1.08)'
    }
  },
  error: {
    marginTop: 10,
    fontSize: 12,
    color: '#b3261e'
  }
}));

const mutation = graphql`
  mutation AvatarUploadMutation($fileType: String!, $contentLength: Int!) {
    profileImageUploadRequest(
      input: { fileType: $fileType, contentLength: $contentLength }
    ) {
      successToken
      url
    }
  }
`;

// Uploads any image via the profileImage upload profile and yields the
// success token — the API accepts these tokens for delegate/manager photos
// and company logos alike.
export const uploadProfileImage = (
  file: File,
  onError: (err: string) => void,
  onUploaded: (token: string, url: string) => void
) => {
  const split = file.name.split('.');
  const fileType = split[split.length - 1];

  commitMutation(environment, {
    mutation,
    variables: {
      fileType,
      contentLength: file.size
    },
    onCompleted: async (
      response: {
        profileImageUploadRequest: {
          url: string;
          successToken: string;
        };
      },
      errors: GraphError[]
    ) => {
      if (errors) {
        onError(`${errors[0]?.extensions?.message}`);
        return;
      }
      const data = response.profileImageUploadRequest;
      const uploadResp = await fetch(data.url, {
        method: 'PUT',
        body: file
      });
      if (uploadResp.status !== 200) {
        onError('Unable to upload, please try again');
        return;
      }
      onUploaded(data.successToken, URL.createObjectURL(file));
    },
    onError: () => {
      onError('Unable to upload, please try again');
    }
  });
};

type Props = {
  name: string;
  url?: string;
  readonly?: boolean;
  onUploaded: (token: string, url: string) => void;
};

function AvatarUpload({ name, url, readonly, onUploaded }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  const uploadChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files && evt.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(undefined);
    uploadProfileImage(
      file,
      (err) => {
        setUploading(false);
        setError(err);
      },
      (token, url) => {
        setUploading(false);
        onUploaded(token, url);
      }
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.avatarWrap}>
        <ProfileIcon name={name} url={url} size={68} fontSize={20} />
        {!readonly && (
          <div
            className={classes.cameraBadge}
            onClick={() => inputRef.current && inputRef.current.click()}
            title="Upload photo"
          >
            <FontAwesomeIcon icon={uploading ? faSpinner : faCamera} spin={uploading} />
          </div>
        )}
      </div>
      {!readonly && (
        <input
          accept="image/*"
          className={classes.input}
          onChange={uploadChange}
          type="file"
          ref={inputRef}
        />
      )}
      {!readonly && error && <span className={classes.error}>{error}</span>}
    </div>
  );
}

export default AvatarUpload;
